// Components/Services/HOACommercial/WhoItsFor.jsx
import React from "react";
import { motion } from "framer-motion";
import { FiUsers, FiHome, FiBriefcase, FiShield, FiTrendingUp, FiTool } from "react-icons/fi";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const audience = [
  {
    icon: <FiUsers className="w-5 h-5 text-[#212529]" />,
    title: "HOA Boards",
    desc: "Community boards managing deferred maintenance, building envelope issues, or major capital improvement projects who need a licensed, accountable contractor.",
  },
  {
    icon: <FiHome className="w-5 h-5 text-[#212529]" />,
    title: "Property Management Companies",
    desc: "Property managers overseeing residential or mixed-use communities who need reliable contractors that communicate clearly and complete work on schedule.",
  },
  {
    icon: <FiBriefcase className="w-5 h-5 text-[#212529]" />,
    title: "Commercial Tenants & Landlords",
    desc: "Business owners building out office or retail space, and commercial landlords preparing units for new tenants or upgrading common areas.",
  },
  {
    icon: <FiShield className="w-5 h-5 text-[#212529]" />,
    title: "Insurance & Legal Professionals",
    desc: "Attorneys and insurance adjusters handling construction defect claims or post-damage reconstruction who need a licensed contractor with documented processes.",
  },
  {
    icon: <FiTrendingUp className="w-5 h-5 text-[#212529]" />,
    title: "Real Estate Developers",
    desc: "Developers with light commercial or mixed-use projects who need a general contractor with the capacity and systems to handle complex scopes.",
  },
  {
    icon: <FiTool className="w-5 h-5 text-[#212529]" />,
    title: "Municipalities & Institutions",
    desc: "Public entities and institutional clients that require fully licensed, bonded, and insured contractors with strong documentation and compliance standards.",
  },
];

const WhoItsFor = () => {
  return (
    <section className="bg-[#f8f9fa] py-16 px-6">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.p variants={fadeUp} className="text-[#6c757d] uppercase tracking-widest text-xs font-bold mb-3">
            Is This Right For You?
          </motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-black text-[#212529] leading-tight mb-4">
            Who We Work With
          </motion.h2>
          <motion.p variants={fadeUp} className="text-[#495057] text-base leading-relaxed max-w-2xl mx-auto">
            HOA and commercial work requires a contractor with the right licensing, systems, and communication standards. Here's who we partner with best.
          </motion.p>
        </motion.div>

        {/* 3-column grid */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={staggerContainer}
        >
          {audience.map((item, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              whileHover={{ x: 4, scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="flex items-start gap-4 bg-white rounded-xl px-5 py-5 border border-[#dee2e6] shadow-sm cursor-default"
            >
              <div className="w-10 h-10 rounded-full bg-[#f8f9fa] border border-[#dee2e6] flex items-center justify-center flex-shrink-0 shadow-sm">
                {item.icon}
              </div>
              <div>
                <p className="text-[#212529] font-bold text-sm">{item.title}</p>
                <p className="text-[#6c757d] text-xs mt-1 leading-relaxed">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          className="mt-12 text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <p className="text-[#495057] text-sm mb-4">Have a large-scale project that needs a reliable contractor? Let's talk scope and timeline.</p>
          <a
            href="/contact"
            className="inline-block bg-[#212529] hover:bg-[#343a40] text-white text-sm font-semibold uppercase tracking-wider px-8 py-3 rounded-full transition duration-300"
          >
            Talk to Our Team →
          </a>
        </motion.div>

      </div>
    </section>
  );
};

export default WhoItsFor;